import { create } from 'zustand';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNativeApp, nativeMessage, SdDevices } from './native';

/**
 * Weighing scale: over Web Serial (USB, desktop Chrome / Edge) or, in the Android app, a paired
 * Bluetooth scale (SPP). Most retail scales stream lines like "ST,GS,+  1.250kg" or "  1250 g";
 * we keep the latest reading.
 */
interface SerialPortLike {
  open(o: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  readable: ReadableStream<Uint8Array> | null;
  getInfo?(): { usbVendorId?: number; usbProductId?: number };
}
interface SerialLike { requestPort(): Promise<SerialPortLike>; getPorts(): Promise<SerialPortLike[]> }
const serial = (): SerialLike | undefined => (navigator as unknown as { serial?: SerialLike }).serial;

interface ScaleState {
  supported: boolean;
  connected: boolean;
  grams: number | null;
  stable: boolean;
  error: string | null;
  /** 'usb' (Web Serial) or 'bluetooth' (Android app) */
  via: 'usb' | 'bluetooth' | null;
  name: string | null;
}
export const useScale = create<ScaleState>(() => ({ supported: !!serial() || isNativeApp(), connected: false, grams: null, stable: false, error: null, via: null, name: null }));

let port: SerialPortLike | null = null;
let reader: ReadableStreamDefaultReader<string> | null = null;
let nativeSub: PluginListenerHandle[] = [];
const SAVED = 'sd.scaleDevice';
type Saved = { via: 'usb'; vendor?: number; product?: number } | { via: 'bluetooth'; address: string; name: string };
const saved = (): Saved | null => { try { return JSON.parse(localStorage.getItem(SAVED) ?? 'null'); } catch { return null; } };
const remember = (v: Saved | null) => { try { if (v) localStorage.setItem(SAVED, JSON.stringify(v)); else localStorage.removeItem(SAVED); } catch { /* storage blocked */ } };

/** Parse one line of scale output to grams; null if it holds no weight. */
export function parseScaleLine(line: string): { grams: number; stable: boolean } | null {
  const m = /([-+]?\s*\d+(?:[.,]\d+)?)\s*(kg|g|lb)?/i.exec(line);
  if (!m) return null;
  const n = parseFloat(m[1].replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  const unit = (m[2] ?? (m[1].includes('.') ? 'kg' : 'g')).toLowerCase();
  const grams = Math.round(unit === 'kg' ? n * 1000 : unit === 'lb' ? n * 453.592 : n);
  return { grams, stable: !/\b(US|UNSTABLE|M)\b/i.test(line) };
}

async function readLoop(p: SerialPortLike) {
  const decoder = new TextDecoderStream();
  void p.readable!.pipeTo(decoder.writable as unknown as WritableStream<Uint8Array>).catch(() => {});
  reader = decoder.readable.getReader();
  const feed = lineFeeder();
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      feed(value);
    }
  } catch {
    useScale.setState({ connected: false, error: 'Scale disconnected' });
  }
}

/** Splits a byte/text stream into lines and keeps the newest weight. */
function lineFeeder() {
  let buf = '';
  return (chunk: string) => {
    buf += chunk;
    const lines = buf.split(/[\r\n]+/);
    buf = lines.pop() ?? '';
    if (buf.length > 200) buf = buf.slice(-200);
    for (const l of lines) {
      const r = parseScaleLine(l);
      if (r) useScale.setState({ grams: r.grams, stable: r.stable });
    }
  };
}

async function start(p: SerialPortLike) {
  const baud = Number(localStorage.getItem('sd.scaleBaud') ?? '9600');
  await p.open({ baudRate: baud });
  port = p;
  useScale.setState({ connected: true, error: null, via: 'usb', name: 'USB scale' });
  void readLoop(p);
}

export async function connectScale() {
  const s = serial();
  if (!s) return useScale.setState({ error: 'This browser can’t talk to a USB scale. Use Chrome or Edge on a computer, or a Bluetooth scale in the Android app.' });
  try {
    const p = await s.requestPort();
    await start(p);
    const info = p.getInfo?.() ?? {};
    remember({ via: 'usb', vendor: info.usbVendorId, product: info.usbProductId });
  } catch (e) {
    useScale.setState({ error: (e as Error).name === 'NotFoundError' ? 'No scale was picked.' : (e as Error).message });
  }
}

/** Android app: connect a paired Bluetooth scale and listen to its readings. */
export async function connectBluetoothScale(address: string, name: string) {
  try {
    await stopNative();
    const feed = lineFeeder();
    nativeSub = [
      await SdDevices.addListener('data', (d) => { if (d.id === 'scale') feed(d.text); }),
      await SdDevices.addListener('disconnected', (d) => { if (d.id === 'scale') useScale.setState({ connected: false, error: 'Scale disconnected' }); }),
    ];
    await SdDevices.connect({ id: 'scale', address });
    useScale.setState({ connected: true, error: null, via: 'bluetooth', name });
    remember({ via: 'bluetooth', address, name });
  } catch (e) {
    await stopNative();
    useScale.setState({ connected: false, error: nativeMessage(e) });
  }
}

async function stopNative() {
  for (const h of nativeSub) await h.remove().catch(() => {});
  nativeSub = [];
}

/** Reconnect silently to the scale used last time (matched by USB ids, so a USB printer is never taken for it). */
export async function autoConnectScale() {
  const last = saved();
  if (!last || port || useScale.getState().connected) return;
  if (last.via === 'bluetooth') { if (isNativeApp()) await connectBluetoothScale(last.address, last.name); return; }
  const s = serial();
  if (!s) return;
  const ports = await s.getPorts().catch(() => []);
  const p = ports.find((x) => !last.vendor || (x.getInfo?.().usbVendorId === last.vendor && x.getInfo?.().usbProductId === last.product));
  if (p) await start(p).catch(() => {});
}

export async function disconnectScale() {
  await reader?.cancel().catch(() => {});
  await port?.close().catch(() => {});
  port = null;
  if (useScale.getState().via === 'bluetooth') { await SdDevices.disconnect({ id: 'scale' }).catch(() => {}); await stopNative(); }
  remember(null);
  useScale.setState({ connected: false, grams: null, via: null, name: null });
}
